import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { ExamQuestion } from "@/hooks/useExams";

const LETTERS = ["A", "B", "C", "D", "E"];

interface ReviewPayload {
  examName: string;
  questions: ExamQuestion[];
  answers: Record<number, string>;
  correct: number;
  wrong: number;
  blank: number;
  net: number;
}

function loadReview(): ReviewPayload | null {
  try {
    const raw = sessionStorage.getItem("dd_son_deneme");
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ReviewPayload;
    if (!Array.isArray(parsed.questions) || parsed.questions.length === 0) return null;
    return parsed;
  } catch {
    return null;
  }
}

export default function DenemeSonucPage() {
  const { id } = useParams<{ id: string }>();
  const [review] = useState<ReviewPayload | null>(() => loadReview());
  const [showReview, setShowReview] = useState(false);

  const subjectBreakdown = useMemo(() => {
    if (!review) return [];
    const map = new Map<string, { correct: number; wrong: number; blank: number }>();
    for (const q of review.questions) {
      const entry = map.get(q.lessonName) ?? { correct: 0, wrong: 0, blank: 0 };
      const a = review.answers[q.id];
      if (!a) entry.blank += 1;
      else if (a === q.correctAnswer) entry.correct += 1;
      else entry.wrong += 1;
      map.set(q.lessonName, entry);
    }
    return Array.from(map.entries()).map(([subject, v]) => ({
      subject,
      ...v,
      net: Math.round((v.correct - v.wrong / 3) * 100) / 100,
    }));
  }, [review]);

  if (!review) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-20">
        <span className="w-16 h-16 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
          <i className="ri-file-search-line text-2xl" aria-hidden="true"></i>
        </span>
        <h1 className="mt-5 text-xl font-bold text-foreground-950">Sonuç bulunamadı</h1>
        <p className="mt-2 text-sm text-foreground-600 max-w-md">
          Bu denemeye ait sonuç bulunamadı. Denemeyi yeniden çözüp sonucu burada görebilirsin.
        </p>
        <Link
          to="/ogrenci/deneme-sinavlari"
          className="mt-6 whitespace-nowrap px-5 py-2.5 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
        >
          Denemelere Dön
        </Link>
      </div>
    );
  }

  const total = review.questions.length;
  const pct = total > 0 ? Math.round((review.correct / total) * 100) : 0;

  return (
    <div className="space-y-8">
      {/* Başlık */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-700 bg-primary-100 px-2.5 py-1 rounded-full">
            <i className="ri-checkbox-circle-line" aria-hidden="true"></i>
            Sınav tamamlandı
          </span>
          <h1 className="mt-2 text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
            {review.examName}
          </h1>
          <p className="text-sm md:text-base text-foreground-600 mt-1">
            Deneme sonucun ve ders bazlı dağılımın.
          </p>
        </div>
        <Link
          to="/ogrenci/deneme-sinavlari"
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md border border-background-200 bg-background-50 text-sm font-semibold text-foreground-700 hover:bg-background-100 whitespace-nowrap"
        >
          <i className="ri-arrow-left-line" aria-hidden="true"></i>
          Denemelere Dön
        </Link>
      </div>

      {/* Özet kartları */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-background-50 rounded-xl border border-background-200 p-5 text-center">
          <p className="text-3xl font-bold text-secondary-700">{review.correct}</p>
          <p className="text-xs text-foreground-500 mt-1">Doğru</p>
        </div>
        <div className="bg-background-50 rounded-xl border border-background-200 p-5 text-center">
          <p className="text-3xl font-bold text-red-600">{review.wrong}</p>
          <p className="text-xs text-foreground-500 mt-1">Yanlış</p>
        </div>
        <div className="bg-background-50 rounded-xl border border-background-200 p-5 text-center">
          <p className="text-3xl font-bold text-foreground-400">{review.blank}</p>
          <p className="text-xs text-foreground-500 mt-1">Boş</p>
        </div>
        <div className="bg-background-50 rounded-xl border border-background-200 p-5 text-center">
          <p className="text-3xl font-bold text-primary-700">{review.net}</p>
          <p className="text-xs text-foreground-500 mt-1">Net</p>
        </div>
        <div className="bg-accent-50 rounded-xl border border-accent-200 p-5 text-center">
          <p className="text-3xl font-bold text-accent-700">%{pct}</p>
          <p className="text-xs text-foreground-500 mt-1">Başarı</p>
        </div>
      </div>

      {/* Ders bazlı dağılım */}
      {subjectBreakdown.length > 0 && (
        <section>
          <h2 className="font-heading font-semibold text-lg text-foreground-950 mb-4">Ders Bazlı Dağılım</h2>
          <div className="bg-background-50 rounded-xl border border-background-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200 bg-background-100/60">
                    <th className="text-left font-semibold text-foreground-600 px-5 py-3">Ders</th>
                    <th className="text-center font-semibold text-foreground-600 px-4 py-3">Doğru</th>
                    <th className="text-center font-semibold text-foreground-600 px-4 py-3">Yanlış</th>
                    <th className="text-center font-semibold text-foreground-600 px-4 py-3">Boş</th>
                    <th className="text-center font-semibold text-foreground-600 px-4 py-3">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {subjectBreakdown.map((s) => (
                    <tr key={s.subject} className="border-b border-background-100 last:border-0">
                      <td className="px-5 py-3.5 font-medium text-foreground-900">{s.subject}</td>
                      <td className="text-center px-4 py-3.5 text-secondary-700 font-semibold">{s.correct}</td>
                      <td className="text-center px-4 py-3.5 text-red-600 font-semibold">{s.wrong}</td>
                      <td className="text-center px-4 py-3.5 text-foreground-400">{s.blank}</td>
                      <td className="text-center px-4 py-3.5 font-semibold text-primary-700">{s.net}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* Soru incelemesi */}
      <section>
        <button
          type="button"
          onClick={() => setShowReview((v) => !v)}
          className="inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-800"
        >
          <i className={showReview ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} aria-hidden="true"></i>
          {showReview ? "Soruları gizle" : "Soruları incele"}
        </button>

        {showReview && (
          <div className="mt-4 space-y-4">
            {review.questions.map((q, idx) => {
              const answer = review.answers[q.id];
              const isCorrect = answer === q.correctAnswer;
              const isBlank = !answer;
              return (
                <div
                  key={q.id}
                  className={`bg-background-50 rounded-xl border p-5 ${
                    isCorrect ? "border-secondary-200" : isBlank ? "border-background-200" : "border-red-200"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-sm md:text-base text-foreground-950 leading-relaxed font-medium">
                      <span className="text-foreground-400 mr-1.5">{idx + 1}.</span>
                      {q.questionText}
                    </p>
                    <span
                      className={`shrink-0 inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${
                        isCorrect
                          ? "bg-secondary-100 text-secondary-800"
                          : isBlank
                          ? "bg-background-100 text-foreground-500"
                          : "bg-red-50 text-red-600"
                      }`}
                    >
                      <i
                        className={isCorrect ? "ri-check-line" : isBlank ? "ri-subtract-line" : "ri-close-line"}
                        aria-hidden="true"
                      ></i>
                      {isCorrect ? "Doğru" : isBlank ? "Boş" : "Yanlış"}
                    </span>
                  </div>

                  <div className="mt-3 space-y-1.5">
                    {q.options.map((opt, oi) => {
                      const letter = LETTERS[oi];
                      const isRight = letter === q.correctAnswer;
                      const isPicked = letter === answer;
                      return (
                        <div
                          key={letter}
                          className={`flex items-start gap-2 rounded-md px-3 py-2 text-sm ${
                            isRight
                              ? "bg-secondary-50 text-secondary-900"
                              : isPicked
                              ? "bg-red-50 text-red-700"
                              : "text-foreground-600"
                          }`}
                        >
                          <span className="font-bold shrink-0">{letter})</span>
                          <span className="leading-relaxed">{opt}</span>
                          {isRight && (
                            <i className="ri-check-line ml-auto text-secondary-700 shrink-0" aria-hidden="true"></i>
                          )}
                          {isPicked && !isRight && (
                            <i className="ri-close-line ml-auto text-red-500 shrink-0" aria-hidden="true"></i>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}